import { Component, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { BasePageComponent } from '../../../_shared/components/page/base-page.component';
import { AccountFilterData, WidgetFilterData } from '../../../_shared/models/filters/widget-filter-data.model';
import { AccountFilterComponent } from '../../../_shared/components/filters/account-filter/account-filter.component';
import { FilterService } from '../../../_shared/services/filter.service';
import { isEmptyObject } from '../../../_shared/helpers/object.helper';
import { WorkOrderRequestsOverviewComponent } from './components/work-order-requests-overview/work-order-requests-overview.component';
import { WorkOrdersOverviewComponent } from './components/work-orders-overview/work-orders-overview.component';
import { WorkOrderTabType } from '../../_shared/models/tabs.models';
import { CreateWorkOrderModalComponent } from './components/modals/create-work-order-modal/create-work-order-modal.component';
import { CurrencyHelper } from '../../../_shared/helpers/currency/currency-map.helper';
import { TranslocoDirective } from '@jsverse/transloco';

@Component({
  selector: 'app-work-order',
  standalone: true,
  imports: [
    CommonModule,
    AccountFilterComponent,
    WorkOrdersOverviewComponent,
    WorkOrderRequestsOverviewComponent,
    TranslocoDirective
  ],
  templateUrl: './work-order.component.html',
  styleUrl: './work-order.component.scss'
})
export class WorkOrderComponent extends BasePageComponent {
  @ViewChild(WorkOrderRequestsOverviewComponent) requestsOverview?: WorkOrderRequestsOverviewComponent;
  @ViewChild(WorkOrdersOverviewComponent) workOrdersOverview?: WorkOrdersOverviewComponent;

  private filterService = inject(FilterService);
  private modalService = inject(NgbModal);

  // Tab state
  activeTab: WorkOrderTabType = 'requests';

  // Account filter state
  accountFilter: AccountFilterData;

  // Currency
  currencySymbol: string = '';

  constructor() {
    super();
    this.accountFilter = { accountIds: [] } as AccountFilterData;
  }

  override ngOnInit(): void {
    super.ngOnInit();
    const currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(currencyCode);
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    if (!isEmptyObject(filter)) {
      this.accountFilter = filter;
      this.filterService.updateFilter({
        ...this.accountFilter
      } as WidgetFilterData);

      this.updatePageFilterState();
    }
  }

  onTabChange(tab: WorkOrderTabType): void {
    if (this.activeTab !== tab) {
      this.activeTab = tab;
      this.updatePageFilterState();

      // Re-emit filter after change detection so the child component reloads
      if (!isEmptyObject(this.accountFilter)) {
        setTimeout(() => {
          this.filterService.updateFilter({
            ...this.accountFilter
          } as WidgetFilterData);
        });
      }
    }
  }

  bulkApprove(): void {
    this.requestsOverview?.bulkApprove();
  }

  // Navigation
  openWorkshopCalendar(): void {
    this.router.navigate(['/asset-management/work-order/workshop-calendar']);
  }

  openCreateWorkOrder(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      windowClass: 'create-work-order-modal'
    };

    const modalRef = this.modalService.open(CreateWorkOrderModalComponent, modalOptions);

    modalRef.result.then((result) => {
      if (result) {
        if (this.activeTab === 'requests') {
          this.requestsOverview?.loadData();
        } else {
          this.workOrdersOverview?.loadData();
        }
      }
    });
  }

  protected override onRefresh(): void { }

  protected override shouldRefresh(): boolean { return false; }

  protected override getFilters(): any {
    return {
      ...this.accountFilter,
      activeTab: this.activeTab
    };
  }

  protected override applyPageFilterState(pageFilter: any): void {
    this.accountFilter = { accountIds: pageFilter.accountIds } as AccountFilterData;
    this.activeTab = pageFilter.activeTab ?? 'requests';
  }
}
